import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { ServiceChangeRequestService } from 'src/app/core/services/service-change-request.service';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Subject,
  takeUntil,
  map,
} from 'rxjs';
import {
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  IUiAlertContent,
  TUiAlertTypes,
} from '@irembo-andela/irembogov3-common';
import { HttpErrorResponse } from '@angular/common/http';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import {
  JourneyModel,
  ServiceOption,
  FieldData,
  FieldOption,
  FieldUpdateContext,
  ServiceModel,
  FieldPathConfig,
  DEFAULT_FIELD_PATH,
  ATTACHMENT_FIELD_PATH,
} from '../../../../../core/models/journey.model';
import { JsonEditorComponent, JsonEditorOptions } from '@maaxgr/ang-jsoneditor';

// Utility functions for field configuration
const createFieldOption = (label: string, value: string): FieldOption => ({
  label,
  value,
});

const updateFieldOptions = (
  field: FormlyFieldConfig,
  options: FieldOption[],
  cdr: ChangeDetectorRef
): void => {
  if (field.props) {
    field.props = {
      ...field.props,
      options,
    };
    cdr.detectChanges();
  }
};

// Hook utility functions
const createServiceOptionsHook = (
  context: FieldUpdateContext,
  getOptions: () => FieldOption[]
): void => {
  const { field, cdr, destroy$ } = context;

  const updateOptions = () => {
    const options = getOptions();
    updateFieldOptions(field, options, cdr);

    // Force change detection for custom components
    if (field.type === 'customserviceselector') {
      // Trigger field change event for custom service selector
      if (field.options?.fieldChanges) {
        field.options.fieldChanges.next({
          field,
          type: 'expressionChanges',
          property: 'props.options',
          value: options,
        });
      }
    }
  };

  // Initial update
  updateOptions();

  // Update when model changes
  if (field.options?.formState?.modelChange) {
    field.options.formState.modelChange = () => {
      updateOptions();
    };
  }
};

const createServiceChangeHook = (
  context: FieldUpdateContext,
  onServiceChange: (serviceCode: string) => void
): void => {
  const { field, destroy$ } = context;

  if (field.formControl) {
    field.formControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(destroy$))
      .subscribe((serviceCode: string) => {
        if (serviceCode) {
          onServiceChange(serviceCode);
        }
      });
  }
};

const createOrderBasedHideExpression = (order?: number): boolean => {
  return order === 1;
};

@Component({
  selector: 'irembogov-journeys',
  templateUrl: './journeys.component.html',
  styleUrls: ['./journeys.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class JourneysComponent implements OnInit, OnDestroy {
  @Input() changeRequest: any;
  @Output() submitted = new EventEmitter<boolean>();
  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  @ViewChild('jsonEditor') jsonEditor!: JsonEditorComponent;

  form = new FormGroup({});
  model: JourneyModel = {
    serviceSequence: [],
    triggerParentDefinition: [],
  };
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];

  loadingServices = false;
  loadingFields = false;
  serviceOptions: ServiceOption[] = [];
  serviceOptionsChange = new Subject<void>();

  // Separate array for dropdown options (can be search results or full list)
  dropdownOptions: ServiceOption[] = [];
  dropdownOptionsChange = new Subject<void>();

  // Pagination properties for service loading
  currentServicePage = 0;
  servicePageSize = 20;
  hasMoreServices = true;
  serviceSearchTerm = '';
  loadingMoreServices = false;
  allServicesLoaded = false;

  selectedServiceFields: Map<string, FieldData[]> = new Map();
  currentServiceFields: Map<string, FieldData[]> = new Map();
  selectedServiceAttachments: Map<string, FieldData[]> = new Map();
  currentServiceAttachments: Map<string, FieldData[]> = new Map();

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  showConfigFormErrorMessage = false;
  isLoading = false;

  // JSON Editor properties
  jsonEditorOptions = new JsonEditorOptions();
  jsonEditorErrors: any[] = [];
  private jsonEditorInitialized = false;

  private destroy$ = new Subject<void>();

  // Solution 1: Debounced Model Synchronization properties
  private modelUpdateSubject = new Subject<JourneyModel>();
  private isUpdatingFromEditor = false;
  private isUpdatingFromForm = false;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {
    // Initialize JSON Editor Options
    this.jsonEditorOptions.mode = 'code';
    this.jsonEditorOptions.modes = ['code', 'tree'];
    this.jsonEditorOptions.onChange = () => {
      // This will be called when the editor content changes
      this.updateModelFromEditor();
    };
  }

  ngOnInit(): void {
    this.form = new FormGroup({});
    this.fields = [];

    this.hasMoreServices = true;
    this.allServicesLoaded = false;
    this.serviceSearchTerm = '';

    // Setup centralized model update handling
    this.setupModelSynchronization();

    this.loadServices();
    this.loadServiceMappingData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupModelSynchronization(): void {
    // Debounced model updates to prevent race conditions
    this.modelUpdateSubject
      .pipe(
        debounceTime(150), // Small delay to batch rapid updates
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(newModel => {
        this.model = { ...newModel };

        // Update both form and JSON editor in a coordinated way
        this.updateDependentComponents();
        this.cdr.detectChanges();
      });
  }

  private setupFormValueChangesSubscription(): void {
    // Simple approach: Watch the form's model directly with periodic checks
    // This catches all changes regardless of nesting level
    let lastModelSnapshot = JSON.stringify(this.form.value);

    const checkModelChanges = () => {
      if (!this.isUpdatingFromEditor) {
        const currentModelSnapshot = JSON.stringify(this.form.value);

        if (currentModelSnapshot !== lastModelSnapshot) {
          this.isUpdatingFromForm = true;
          const updatedModel = this.createModelFromForm(this.form.value);

          // Update the component model directly
          this.model = { ...updatedModel };

          // Also trigger the model update subject for other subscribers
          this.modelUpdateSubject.next(updatedModel);

          // Directly update the JSON editor
          this.updateEditorFromModel();

          setTimeout(() => (this.isUpdatingFromForm = false), 50);

          lastModelSnapshot = currentModelSnapshot;
        }
      }
    };

    // Check for model changes every 200ms - this catches all nested changes
    const modelWatchInterval = setInterval(checkModelChanges, 200);

    // Also listen to main form changes for immediate updates (for non-nested changes)
    this.form.valueChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        // Trigger immediate check
        checkModelChanges();
      });

    // Clean up the interval when component is destroyed
    this.destroy$.subscribe(() => {
      clearInterval(modelWatchInterval);
    });
  }

  private createModelFromForm(formValue: any): JourneyModel {
    return {
      serviceSequence: formValue.serviceSequence || [],
      triggerParentDefinition: formValue.triggerParentDefinition || [],
    };
  }

  private updateDependentComponents(): void {
    // Always update the JSON editor from the model
    this.updateEditorFromModel();

    // Update form only if editor triggered the change
    if (this.isUpdatingFromEditor && this.options.updateInitialValue) {
      this.options.updateInitialValue();
    }

    // Trigger field options refresh for all dependent fields
    this.refreshAllFieldOptions();
  }

  private refreshAllFieldOptions(): void {
    // Emit service options change to trigger all dependent field updates
    this.serviceOptionsChange.next();

    // Force change detection on custom service selectors
    this.fields.forEach(field => this.triggerFieldRefresh(field));
  }

  private triggerFieldRefresh(field: FormlyFieldConfig): void {
    // Skip if field is a function type
    if (typeof field === 'function') {
      return;
    }

    if (field.fieldGroup) {
      field.fieldGroup.forEach(subField => {
        // Skip if subField is a function type
        if (typeof subField !== 'function') {
          this.triggerFieldRefresh(subField);
        }
      });
    }

    if ((field.fieldArray as any)?.fieldGroup) {
      (field.fieldArray as any).fieldGroup.forEach((subField: any) => {
        // Skip if subField is a function type
        if (typeof subField !== 'function') {
          this.triggerFieldRefresh(subField);
        }
      });
    }

    if (field.type === 'customserviceselector' && field.options?.fieldChanges) {
      field.options.fieldChanges.next({
        field,
        type: 'expressionChanges',
        property: 'props.options',
        value: (field.props as any)?.options || [],
      });
    }
  }

  updateEditorFromModel(): void {
    if (!this.jsonEditor) {
      return;
    }

    try {
      // Create a deep copy of the model to avoid reference issues
      const modelCopy = JSON.parse(JSON.stringify(this.model));

      if (this.jsonEditorInitialized) {
        // Try to update the existing editor
        const editorInstance = this.jsonEditor.getEditor();
        if (editorInstance) {
          // Use set instead of update to force a complete refresh
          editorInstance.set(modelCopy);
        } else {
          // Fallback: reinitialize the editor
          this.jsonEditor.set(modelCopy);
        }
      } else {
        // Initialize the editor with the model
        this.jsonEditor.set(modelCopy);
        this.jsonEditorInitialized = true;
      }

      // Force change detection to ensure UI updates
      this.cdr.detectChanges();
    } catch (error) {
      // Try to reinitialize on error
      try {
        this.jsonEditor.set(JSON.parse(JSON.stringify(this.model)));
        this.jsonEditorInitialized = true;
        this.cdr.detectChanges();
      } catch (retryError) {
        // Failed to reinitialize JSON editor - silent fail
      }
    }
  }

  updateModelFromEditor(): void {
    if (!this.jsonEditor || this.isUpdatingFromForm) return;

    try {
      // Get the current editor instance
      const editorJson = this.jsonEditor.getEditor();
      if (!editorJson) return;

      // Validate the JSON content
      const validationErrors = editorJson.validate();

      // If there are validation errors, store them and don't update the model
      if (validationErrors && validationErrors.length > 0) {
        this.jsonEditorErrors = validationErrors;
        return;
      }

      // Clear previous errors
      this.jsonEditorErrors = [];

      // Get the current data and update the model
      const jsonData = editorJson.get();

      this.isUpdatingFromEditor = true;
      this.modelUpdateSubject.next(JSON.parse(JSON.stringify(jsonData)));
      setTimeout(() => (this.isUpdatingFromEditor = false), 50);
    } catch (error: any) {
      this.jsonEditorErrors = error.validationErrors || [
        { message: error.message || 'Error parsing JSON' },
      ];
    }
  }

  onJsonEditorChange(event: any): void {
    if (event && event.isTrusted) return;

    // Update the model from the editor
    this.updateModelFromEditor();
  }

  loadServices(search?: string, append = false): void {
    if (this.loadingServices || this.loadingMoreServices) {
      return;
    }

    let searchTermToUse = search;

    if (search === undefined) {
      searchTermToUse = undefined;
      this.serviceSearchTerm = '';
      this.currentServicePage = 0;
      this.servicePageSize = 20;
      this.hasMoreServices = true;
      this.allServicesLoaded = false;
      append = false;
    }
    if (search !== this.serviceSearchTerm) {
      this.serviceSearchTerm = search || '';
      this.currentServicePage = 0;
      this.servicePageSize = 20;
      this.hasMoreServices = true;
      this.allServicesLoaded = false;
      append = false;
      searchTermToUse = search || '';
    }

    if (append) {
      searchTermToUse = this.serviceSearchTerm || undefined;
    }

    if (
      this.allServicesLoaded &&
      searchTermToUse === this.serviceSearchTerm &&
      append
    ) {
      return;
    }

    const pageToRequest = 0;
    let sizeToRequest = this.servicePageSize;

    if (append && this.hasMoreServices) {
      sizeToRequest = this.servicePageSize + this.dropdownOptions.length;
      this.loadingMoreServices = true;
    } else {
      this.loadingServices = true;
    }

    this.serviceChangeRequestService
      .getServicesWithPagination(
        pageToRequest,
        sizeToRequest,
        searchTermToUse || undefined
      )
      .pipe(
        finalize(() => {
          this.loadingServices = false;
          this.loadingMoreServices = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response && response.data) {
            const newServices = response.data.content.map(service => ({
              id: service.id,
              serviceName: service.serviceName,
              changeRequestId: service.changeRequestId,
              serviceCode: service.serviceCode,
            }));

            // Update dropdownOptions instead of serviceOptions
            this.dropdownOptions = newServices;
            this.dropdownOptionsChange.next(); // Emit dropdown change

            this.servicePageSize = sizeToRequest;
            this.currentServicePage = 0;
            this.hasMoreServices = !response.data.last;
            this.allServicesLoaded = response.data.last;

            this.serviceOptionsChange.next();

            // Ensure all model services are present in options
            this.ensureModelServicesInOptions();

            if (this.fields.length === 0) {
              this.initFormFields();
            }
          }
        },
        error: error => {
          // Error loading services - silent fail
        },
      });
  }

  private addServiceToSelected(service: ServiceOption): void {
    // Check if service is already in selected services
    const existingService = this.serviceOptions.find(
      s => s.serviceCode === service.serviceCode
    );

    if (!existingService) {
      // Add to selected services
      this.serviceOptions.push(service);
      this.serviceOptionsChange.next();
    }
  }

  loadServiceFormFields(
    serviceCode: string,
    changeRequestId: string,
    isCurrentService = false
  ): void {
    if (!changeRequestId) {
      return;
    }

    this.loadingFields = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestFormDefinition(changeRequestId)
      .pipe(
        finalize(() => (this.loadingFields = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response && response.data) {
            const allFields: FieldData[] = [];
            const allAttachments: FieldData[] = [];

            // Extract fields from the form definition
            Object.values(response.data).forEach(sections => {
              this.extractFields(sections, allFields, allAttachments);
            });

            // Store fields based on whether it's the current service or a source service
            if (isCurrentService) {
              this.currentServiceFields.set(serviceCode, allFields);
              this.currentServiceAttachments.set(serviceCode, allAttachments);
            } else {
              this.selectedServiceFields.set(serviceCode, allFields);
              this.selectedServiceAttachments.set(serviceCode, allAttachments);
            }

            // Update field mapping options for dependent services
            this.updateFieldMappingOptions();
          }
        },
        error: error => {
          // Error loading form fields - silent fail
        },
      });
  }

  extractFields(
    sections: any[],
    allFields: FieldData[],
    allAttachments: FieldData[]
  ): void {
    if (!sections || !Array.isArray(sections)) {
      return;
    }

    sections.forEach(section => {
      if (section.fieldGroup) {
        section.fieldGroup.forEach((formGroup: any) => {
          if (formGroup.fieldGroup) {
            formGroup.fieldGroup.forEach((block: any) => {
              if (block.fieldGroup) {
                block.fieldGroup.forEach((field: any) => {
                  if (field.key && field.props?.label) {
                    const fieldData: FieldData = {
                      fieldName: field.key,
                      fieldLabel: field.props.label,
                    };

                    // Separate attachment fields from regular fields
                    if (field.type === 'customfileupload') {
                      allAttachments.push(fieldData);
                    } else {
                      // Only add non-attachment fields
                      allFields.push(fieldData);
                    }
                  }
                });
              }

              if (block.fieldArray) {
                block.fieldArray.forEach((fieldArray: any) => {
                  if (fieldArray.arrayItems) {
                    fieldArray.arrayItems.forEach((arrayItem: any) => {
                      // Process fieldGroup within array items
                      if (arrayItem.fieldGroup) {
                        arrayItem.fieldGroup.forEach((field: any) => {
                          if (field.key && field.props?.label) {
                            const fieldData: FieldData = {
                              fieldName: field.fieldName || field.key,
                              fieldLabel:
                                field.fieldLabel || field.props?.label,
                            };

                            if (field.type === 'customfileupload') {
                              allAttachments.push(fieldData);
                            } else {
                              allFields.push(fieldData);
                            }
                          }
                        });
                      }

                      // Process formGroup within array items
                      if (arrayItem.formGroup) {
                        arrayItem.formGroup.forEach((formGroup: any) => {
                          if (formGroup.fieldGroup) {
                            formGroup.fieldGroup.forEach((field: any) => {
                              if (field.key && field.props?.label) {
                                const fieldData: FieldData = {
                                  fieldName: field.fieldName || field.key,
                                  fieldLabel:
                                    field.fieldLabel || field.props?.label,
                                };

                                if (field.type === 'customfileupload') {
                                  allAttachments.push(fieldData);
                                } else {
                                  allFields.push(fieldData);
                                }
                              }
                            });
                          }
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  updateFieldMappingOptions(): void {
    // Find the current serviceDefinitions field
    const serviceDefsField = this.fields[0] as any; // Cast to any to bypass type checking
    const serviceSequence = serviceDefsField?.fieldGroup?.find(
      (f: any) => f.key === 'serviceSequence'
    );

    if (serviceSequence?.fieldArray?.fieldGroup) {
      // Trigger form options update
      this.options.updateInitialValue?.();
      this.cdr.detectChanges();
    }
  }

  getServiceByCode(serviceCode: string): ServiceOption | undefined {
    if (!serviceCode) return undefined;

    // First check in selected services (serviceOptions)
    let service = this.serviceOptions.find(s => s.serviceCode === serviceCode);

    // If not found, check in dropdown options
    if (!service) {
      service = this.dropdownOptions.find(s => s.serviceCode === serviceCode);
    }

    return service;
  }

  loadServiceWorkflowStates(
    serviceCode: string,
    changeRequestId: string
  ): void {
    if (!changeRequestId) {
      return;
    }

    this.loadingFields = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
        changeRequestId
      )
      .pipe(
        finalize(() => {
          this.loadingFields = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response && response.data) {
            try {
              // Parse workflow to extract available end states
              const workflowData: any = response.data;
              const endStates: string[] = [];

              // Extract workflow states based on the structure from the workflow-state-selector component
              if (workflowData?.['workflow']) {
                const workflow = workflowData?.['workflow'] as any[];

                // Extract unique end states from the workflow
                workflow.forEach(step => {
                  if (step.endStateOne?.stateCode) {
                    endStates.push(step.endStateOne.stateCode);
                  }
                  if (step.endStateTwo?.stateCode) {
                    endStates.push(step.endStateTwo.stateCode);
                  }
                });
              }

              // Get unique end states
              const uniqueEndStates = [...new Set(endStates)];

              // Update the serviceOption
              const serviceOption = this.getServiceByCode(serviceCode);
              if (serviceOption) {
                serviceOption.workflowStates = uniqueEndStates;

                // Notify subscribers that service options have changed
                this.serviceOptionsChange.next();
              }
            } catch (error) {
              // Error loading workflow states - silent fail
            }
          }
        },
        error: error => {
          // Error loading workflow states - silent fail
        },
      });
  }

  getAvailableSourceServices(currentOrder: number): ServiceOption[] {
    // Only services with lower order numbers can be sources
    if (!currentOrder || currentOrder <= 1) {
      return [];
    }

    // Get services with lower order numbers
    const sourceDefinitions = this.model.serviceSequence.filter(
      s => s.order < currentOrder && s.serviceCode
    ); // Ensure serviceCode exists

    // Map to service options
    const options = sourceDefinitions
      .map(s => this.getServiceByCode(s.serviceCode))
      .filter((s): s is ServiceOption => !!s); // Type guard to ensure non-null

    return options;
  }

  getSourceServiceFields(serviceCode: string): FieldData[] {
    return this.selectedServiceFields.get(serviceCode) || [];
  }

  getCurrentServiceFields(serviceCode: string): FieldData[] {
    return this.currentServiceFields.get(serviceCode) || [];
  }

  getSourceServiceAttachments(serviceCode: string): FieldData[] {
    return this.selectedServiceAttachments.get(serviceCode) || [];
  }

  getCurrentServiceAttachments(serviceCode: string): FieldData[] {
    return this.currentServiceAttachments.get(serviceCode) || [];
  }

  loadMoreServices(): void {
    if (
      this.hasMoreServices &&
      !this.loadingMoreServices &&
      !this.allServicesLoaded &&
      !this.loadingServices
    ) {
      this.loadServices(this.serviceSearchTerm, true);
    }
  }

  searchServices(searchTerm: string): void {
    if (
      searchTerm === null ||
      searchTerm === undefined ||
      searchTerm.trim() === ''
    ) {
      this.loadServices(undefined, false);
    } else {
      this.loadServices(searchTerm.trim(), false);
    }
  }

  getFilteredServiceOptions(): ServiceOption[] {
    if (!this.model?.serviceSequence) return this.serviceOptions;

    const selectedServiceCodes = this.model.serviceSequence
      .map(s => s.serviceCode)
      .filter(code => code);

    return this.serviceOptions.filter(
      option => !selectedServiceCodes.includes(option.serviceCode)
    );
  }

  initFormFields(): void {
    this.form = new FormGroup({});

    this.fields = [
      {
        fieldGroup: [
          this.createServiceDefinitionsField(),
          this.createTriggerParentDefinitionField(),
        ],
      },
    ];

    // Set up form value changes subscription after a small delay to ensure Formly is fully initialized
    setTimeout(() => {
      this.setupFormValueChangesSubscription();
    }, 100);
  }

  private createTriggerParentDefinitionField(): FormlyFieldConfig {
    return {
      key: 'triggerParentDefinition',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      props: {
        label: 'Trigger Parent Definition',
        addText: 'Add Trigger Parent Definition',
        titleFieldName: 'serviceCode',
        hideLabel: true,
        required: true,
      },
      fieldArray: {
        fieldGroup: [
          {
            fieldGroup: [
              {
                key: 'serviceCode',
                type: 'select',
                props: {
                  label: 'Service Code',
                  required: true,
                  placeholder: 'Select a service',
                  options: [],
                },
                hooks: {
                  onInit: (field: FormlyFieldConfig) => {
                    // Get the current field index
                    const currentIndex = (field.parent?.parent as any)?.index;

                    createServiceOptionsHook(
                      { field, cdr: this.cdr, destroy$: this.destroy$ },
                      () => {
                        if (!this.model?.serviceSequence) return [];

                        // Get previously selected services in trigger parent definitions
                        const selectedTriggerServices =
                          this.model.triggerParentDefinition
                            .filter(
                              (def, index) =>
                                index !== currentIndex && def.serviceCode
                            )
                            .map(def => def.serviceCode);

                        // Get all service codes from the sequence
                        const mappedServiceCodes = this.model.serviceSequence
                          .filter(service => service.serviceCode)
                          .map(service => service.serviceCode);

                        return this.serviceOptions
                          .filter(
                            service =>
                              mappedServiceCodes.includes(
                                service.serviceCode
                              ) &&
                              (!selectedTriggerServices.includes(
                                service.serviceCode
                              ) ||
                                service.serviceCode ===
                                  field.formControl?.value)
                          )
                          .map(service => {
                            const naming = `${service.serviceCode} - ${service.serviceName}`;
                            return createFieldOption(
                              naming,
                              service.serviceCode
                            );
                          });
                      }
                    );

                    createServiceChangeHook(
                      { field, cdr: this.cdr, destroy$: this.destroy$ },
                      (serviceCode: string) => {
                        // Load workflow states for this service if not already loaded
                        const service = this.getServiceByCode(serviceCode);
                        if (service && service.changeRequestId) {
                          this.loadServiceWorkflowStates(
                            service.serviceCode,
                            service.changeRequestId
                          );
                        }
                      }
                    );
                  },
                },
              },
              this.createWorkflowStateSelect(
                {
                  modelPath: 'parent?.parent?.model',
                  fieldGroupPath: 'parent?.fieldGroup',
                },
                {
                  key: 'triggerParentStates',
                  label: 'Trigger Parent Events',
                  placeholder: 'Select events',
                  multiple: true,
                }
              ),
            ],
          },
        ],
      },
    };
  }

  private createServiceDefinitionsField(): FormlyFieldConfig {
    return {
      key: 'serviceSequence',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      props: {
        label: 'Service Sequence',
        addText: 'Add Service',
        required: true,
        titleFieldName: 'serviceName',
        hideLabel: true,
      },
      fieldArray: {
        fieldGroup: [
          {
            fieldGroup: [
              this.createServiceCodeField(),
              this.createServiceNameField(),
              this.createChangeRequestIdField(),
              this.createServiceSourceField(),
              this.createOrderField(),
              this.createRequiredField(),
              this.createRepeatingFieldsMappingField(),
              this.createAttachmentsMappingField(),
              this.createStateMappingField(),
            ],
          },
        ],
      },
    };
  }

  private createServiceCodeField(): FormlyFieldConfig {
    return {
      key: 'serviceCode',
      type: 'customserviceselector',
      props: {
        label: 'Service',
        required: true,
        placeholder: 'Select a service',
        searchPlaceholder: 'Search services...',
        noResultsText: 'No services found',
        loadingText: 'Loading services...',
        loadingMoreText: 'Loading more...',
        options: [],
        excludeIds: [],
        // Custom properties for pagination
        onSearch: (searchTerm: string) => this.searchServices(searchTerm),
        onLoadMore: () => this.loadMoreServices(),
        hasMoreData: () => this.hasMoreServices && !this.allServicesLoaded,
        isLoading: () => this.loadingServices,
        isLoadingMore: () => this.loadingMoreServices,
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          if (field && this.cdr && this.destroy$) {
            const context: FieldUpdateContext = {
              field,
              cdr: this.cdr,
              destroy$: this.destroy$,
            };

            const getFilteredOptionsWithCurrent = () => {
              const currentValue = field.formControl?.value;

              const excludeIds: string[] = [];
              if (this.model?.serviceSequence) {
                this.model.serviceSequence.forEach((serviceDef, index) => {
                  const currentIndex = (field.parent?.parent as any)?.index;
                  if (index !== currentIndex && serviceDef.serviceCode) {
                    excludeIds.push(serviceDef.serviceCode);
                  }
                });
              }

              if (field.props) {
                field.props = {
                  ...field.props,
                  excludeIds: excludeIds,
                };
              }

              let availableServices = [...this.dropdownOptions];

              if (currentValue && excludeIds.includes(currentValue)) {
                availableServices = this.dropdownOptions;
              }

              return availableServices;
            };

            createServiceOptionsHook(context, () =>
              getFilteredOptionsWithCurrent().map(opt => {
                const naming = `${opt.serviceCode} - ${opt.serviceName}`;
                return createFieldOption(naming, opt.serviceCode);
              })
            );

            this.dropdownOptionsChange
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                const filteredServices = getFilteredOptionsWithCurrent();
                const newOptions = filteredServices.map(opt => {
                  const naming = `${opt.serviceCode} - ${opt.serviceName}`;
                  return createFieldOption(naming, opt.serviceCode);
                });
                updateFieldOptions(field, newOptions, this.cdr);
              });

            // Listen to model changes to update excludeIds
            this.modelUpdateSubject
              .pipe(debounceTime(100), takeUntil(this.destroy$))
              .subscribe(() => {
                const filteredServices = getFilteredOptionsWithCurrent();
                const newOptions = filteredServices.map(opt => {
                  const naming = `${opt.serviceCode} - ${opt.serviceName}`;
                  return createFieldOption(naming, opt.serviceCode);
                });
                updateFieldOptions(field, newOptions, this.cdr);
              });

            createServiceChangeHook(context, (serviceCode: string) =>
              this.handleServiceChange(serviceCode)
            );

            setTimeout(() => {
              const currentServiceCode = field.formControl?.value;
              if (currentServiceCode) {
                this.handleServiceChange(currentServiceCode);
              }
            }, 0);
          }
        },
      },
    };
  }

  private createServiceNameField(): FormlyFieldConfig {
    return {
      key: 'serviceName',
      type: 'input',
      props: {
        label: 'Service Name',
        readonly: true,
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const serviceCodeField = field.parent?.fieldGroup?.find(
            f => f.key === 'serviceCode'
          );

          if (serviceCodeField?.formControl && this.destroy$) {
            serviceCodeField.formControl.valueChanges
              .pipe(takeUntil(this.destroy$))
              .subscribe((serviceCode: string) => {
                if (serviceCode) {
                  const service = this.getServiceByCode(serviceCode);
                  if (service?.serviceName) {
                    field.formControl?.setValue(service.serviceName);
                  }
                }
              });
          }
        },
      },
    };
  }

  private createChangeRequestIdField(): FormlyFieldConfig {
    return {
      key: 'changeRequestId',
      type: 'input',
      props: {
        label: 'Change Request ID',
        readonly: true,
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const serviceCodeField = field.parent?.fieldGroup?.find(
            f => f.key === 'serviceCode'
          );

          if (serviceCodeField?.formControl && this.destroy$) {
            serviceCodeField.formControl.valueChanges
              .pipe(takeUntil(this.destroy$))
              .subscribe((serviceCode: string) => {
                if (serviceCode) {
                  const service = this.getServiceByCode(serviceCode);
                  if (service?.changeRequestId) {
                    field.formControl?.setValue(service.changeRequestId);
                  }
                }
              });
          }
        },
      },
    };
  }

  private createServiceSourceField(): FormlyFieldConfig {
    return {
      key: 'serviceSource',
      type: 'select',
      defaultValue: 'IREMBO_3_0',
      props: {
        label: 'Service Source',
        required: true,
        options: [
          { value: 'IREMBO_3_0', label: 'IREMBO 3.0' },
          { value: 'IREMBO_2_5', label: 'IREMBO 2.5' },
        ],
      },
    };
  }

  private createOrderField(): FormlyFieldConfig {
    return {
      key: 'order',
      type: 'input',
      props: {
        type: 'number',
        label: 'Order',
        placeholder: 'Execution order (e.g., 1, 2, 3)',
        description: 'Services with lower numbers are processed first.',
        required: true,
        min: 1,
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const index = (field.parent?.parent as any)?.index || 0;
          field.formControl?.setValue(index + 1);
        },
      },
    };
  }

  private createRequiredField(): FormlyFieldConfig {
    return {
      key: 'required',
      type: 'checkbox',
      defaultValue: true,
      props: {
        label: 'Required Service',
        description:
          'If checked, this service must be completed for the journey to be considered complete.',
      },
    };
  }

  private handleServiceChange(serviceCode: string): void {
    const service = this.getServiceByCode(serviceCode);
    if (!service?.changeRequestId) return;

    // Add service to selected services
    this.addServiceToSelected(service);

    // Load service data asynchronously without immediately updating model
    Promise.all([
      this.loadServiceFormFieldsAsync(
        serviceCode,
        service.changeRequestId,
        true
      ),
      this.loadServiceFormFieldsAsync(
        serviceCode,
        service.changeRequestId,
        false
      ),
      this.loadServiceWorkflowStatesAsync(serviceCode, service.changeRequestId),
    ]).then(() => {
      // All data loaded, trigger a delayed refresh to ensure consistency
      setTimeout(() => this.refreshAllFieldOptions(), 100);
    });
  }

  // Convert loading methods to return promises for better control:
  private loadServiceFormFieldsAsync(
    serviceCode: string,
    changeRequestId: string,
    isCurrentService: boolean
  ): Promise<void> {
    return new Promise(resolve => {
      if (
        (isCurrentService && this.currentServiceFields.has(serviceCode)) ||
        (!isCurrentService && this.selectedServiceFields.has(serviceCode))
      ) {
        resolve();
        return;
      }

      this.serviceChangeRequestService
        .getServiceChangeRequestFormDefinition(changeRequestId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response?.data) {
              const allFields: FieldData[] = [];
              const allAttachments: FieldData[] = [];

              Object.values(response.data).forEach(sections => {
                this.extractFields(sections, allFields, allAttachments);
              });

              if (isCurrentService) {
                this.currentServiceFields.set(serviceCode, allFields);
                this.currentServiceAttachments.set(serviceCode, allAttachments);
              } else {
                this.selectedServiceFields.set(serviceCode, allFields);
                this.selectedServiceAttachments.set(
                  serviceCode,
                  allAttachments
                );
              }
            }
            resolve();
          },
          error: () => resolve(), // Don't fail the promise chain
        });
    });
  }

  private loadServiceWorkflowStatesAsync(
    serviceCode: string,
    changeRequestId: string
  ): Promise<void> {
    return new Promise(resolve => {
      const service = this.getServiceByCode(serviceCode);
      if (service?.workflowStates) {
        resolve();
        return;
      }

      this.serviceChangeRequestService
        .getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
          changeRequestId
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response?.data && service) {
              const workflowData: any = response.data;
              const endStates: string[] = [];

              if (workflowData?.workflow) {
                workflowData.workflow.forEach((step: any) => {
                  if (step.endStateOne?.stateCode)
                    endStates.push(step.endStateOne.stateCode);
                  if (step.endStateTwo?.stateCode)
                    endStates.push(step.endStateTwo.stateCode);
                });
              }

              service.workflowStates = [...new Set(endStates)];
              this.serviceOptionsChange.next();
            }
            resolve();
          },
          error: () => resolve(), // Don't fail the promise chain
        });
    });
  }

  // Utility method to mark all controls as touched to trigger validation
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });

    // Update the JSON editor when form changes
    this.updateEditorFromModel();
  }

  submit(): void {
    this.saveServiceMapping();
  }

  skipJourneyMapping(): void {
    this.toastService.show({
      body: 'Journey mapping skipped',
      type: 'info',
    });
    this.submitted.emit(true);
  }

  saveServiceMapping() {
    this.isLoading = true;
    if (this.form.valid && this.changeRequest?.id) {
      // Process the data for saving
      const journeyConfig = this.prepareDataForSaving();

      this.serviceChangeRequestService
        .updateJourneyServiceMapping(this.changeRequest.id, journeyConfig)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (
            response: IHttpSingleDataResponse<Record<string, unknown>>
          ) => {
            this.toastService.show({
              body:
                response.responseMessage ||
                'Service mapping configuration saved successfully',
              type: 'success',
            });
            this.submitted.emit(true);
          },
          error: (error: HttpErrorResponse) => {
            const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
              ExtractHttpErrorResponseCodeAndMessage(
                error,
                'Failed to save service mapping configuration'
              );

            this.toastService.show({
              body: errorCodeAndMessage.message,
              type: 'error',
            });

            this.updateFormDisplayedErrorMessageContent(
              true,
              'danger',
              'Error Saving Configuration',
              errorCodeAndMessage.message
            );
            this.submitted.emit(false);
          },
        });
    } else {
      this.isLoading = false;
      // Mark form controls as touched to reveal validation errors
      this.markFormGroupTouched(this.form);

      if (!this.form.valid) {
        this.updateFormDisplayedErrorMessageContent(
          true,
          'danger',
          'Validation Error',
          'Please fix the form errors before continuing.'
        );
      }

      this.submitted.emit(false);
    }
  }

  loadServiceMappingData(): void {
    if (!this.changeRequest?.id) return;

    this.loadingServices = true;
    this.serviceChangeRequestService
      .getJourneyServiceMapping(this.changeRequest.id)
      .pipe(
        finalize(() => (this.loadingServices = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          if (response && response.data) {
            // Convert the response data to our model format
            const mappingData = response.data as any;
            if (mappingData) {
              // Initialize the model
              this.model = {
                serviceSequence: mappingData.serviceSequence || [],
                triggerParentDefinition: [],
              };

              // Process triggerParentDefinition if it exists
              if (Array.isArray(mappingData.triggerParentDefinition)) {
                this.model.triggerParentDefinition =
                  mappingData.triggerParentDefinition.map((def: any) => {
                    // Convert string values to array values for triggerParentStates
                    if (typeof def.triggerParentStates === 'string') {
                      // Split comma-separated values into an array
                      return {
                        ...def,
                        triggerParentStates: def.triggerParentStates
                          .split(',')
                          .map((s: string) => s.trim())
                          .filter((s: string) => s),
                      };
                    }
                    // Ensure it's an array
                    if (!Array.isArray(def.triggerParentStates)) {
                      return {
                        ...def,
                        triggerParentStates: [],
                      };
                    }
                    return def;
                  });
              }

              // Ensure all model services are present in options
              this.ensureModelServicesInOptions();

              // Update the JSON editor with the model data
              this.updateEditorFromModel();

              // Update Formly form with the new model
              if (
                this.options &&
                typeof this.options.updateInitialValue === 'function'
              ) {
                this.options.updateInitialValue();
              }

              // For each service definition, load its fields
              if (this.model.serviceSequence?.length > 0) {
                this.model.serviceSequence.forEach(serviceDef => {
                  if (serviceDef.serviceCode && serviceDef.changeRequestId) {
                    // Load fields for the current service
                    this.loadServiceFormFields(
                      serviceDef.serviceCode,
                      serviceDef.changeRequestId,
                      true
                    );

                    // Also load as source fields
                    this.loadServiceFormFields(
                      serviceDef.serviceCode,
                      serviceDef.changeRequestId,
                      false
                    );

                    // Load workflow states
                    this.loadServiceWorkflowStates(
                      serviceDef.serviceCode,
                      serviceDef.changeRequestId
                    );
                  }
                });
              }
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to load service mapping data'
            );

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Error Loading Data',
            errorCodeAndMessage.message
          );
        },
      });
  }

  prepareDataForSaving(): Record<string, unknown> {
    const journeyConfig = { ...this.model };

    // Sort services by order to ensure correct processing sequence
    journeyConfig.serviceSequence.sort((a, b) => a.order - b.order);

    // Process triggerParentDefinition if it exists
    if (
      journeyConfig.triggerParentDefinition &&
      Array.isArray(journeyConfig.triggerParentDefinition)
    ) {
      journeyConfig.triggerParentDefinition =
        journeyConfig.triggerParentDefinition.filter(
          config =>
            config.serviceCode &&
            config.triggerParentStates &&
            config.triggerParentStates.length > 0
        );
    }

    return journeyConfig;
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title = '',
    message = ''
  ): void {
    this.showConfigFormErrorMessage = show;
    this.formAlertContent = { type, title, message };

    if (show && this.errorMessageElement) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  private createRepeatingFieldsMappingField(): FormlyFieldConfig {
    return {
      key: 'repeatingFieldsMapping',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      props: {
        label: 'Repeating Fields Mapping',
        addText: 'Add Field Mapping',
        titleFieldName: 'targetField',
        hideLabel: true,
        description:
          "Define how data should be copied from previous services into this service's form fields.",
      },
      hideExpression: (model: any, formState: any, field?: FormlyFieldConfig) =>
        createOrderBasedHideExpression(field?.parent?.model?.order),
      fieldArray: {
        fieldGroup: [
          {
            fieldGroup: [
              this.createTargetFieldSelect(DEFAULT_FIELD_PATH, true),
              this.createSourceServiceSelect(),
              this.createSourceFieldSelect(true),
            ],
          },
        ],
      },
    };
  }

  private createTargetFieldSelect(
    pathConfig: FieldPathConfig = DEFAULT_FIELD_PATH,
    includeAttachments = false
  ): FormlyFieldConfig {
    return {
      key: 'targetField',
      type: 'select',
      props: {
        label: 'Target Field',
        required: true,
        placeholder: 'Field in current service',
        options: [],
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const getCurrentServiceCode = () => {
            const path = pathConfig.modelPath;
            return eval(`field.${path}?.serviceCode`);
          };

          const updateTargetFieldOptions = () => {
            const currentServiceCode = getCurrentServiceCode();
            if (currentServiceCode) {
              let currentFields =
                this.getCurrentServiceFields(currentServiceCode);

              if (includeAttachments) {
                const attachmentFields =
                  this.getCurrentServiceAttachments(currentServiceCode);
                currentFields = [...currentFields, ...attachmentFields];
              }

              // Get current value if it exists
              const currentValue = field.formControl?.value;

              // Create options array with all fields
              const fieldOptions: FieldOption[] = currentFields.map(f =>
                createFieldOption(f.fieldLabel, f.fieldName)
              );

              // If the current value isn't in the options but exists in model, ensure it's added
              if (
                currentValue &&
                !currentFields.some(f => f.fieldName === currentValue)
              ) {
                fieldOptions.push(
                  createFieldOption(currentValue, currentValue)
                );
              }

              // Update the field options directly
              if (field.props) {
                field.props.options = fieldOptions;
                this.cdr.detectChanges();
              }
            }
          };

          // Initial update
          updateTargetFieldOptions();

          // Update when service changes
          const serviceField = eval(
            `field.${pathConfig.fieldGroupPath}?.find(f => f.key === 'serviceCode')`
          );

          serviceField?.formControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              updateTargetFieldOptions();
            });
        },
      },
    };
  }

  private createSourceServiceSelect(
    pathConfig: FieldPathConfig = DEFAULT_FIELD_PATH
  ): FormlyFieldConfig {
    return {
      key: 'sourceService',
      type: 'select',
      props: {
        label: 'Source Service',
        required: true,
        placeholder: 'Select source service',
        options: [],
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const updateSourceOptions = () => {
            const currentOrder = eval(`field.${pathConfig.modelPath}?.order`);
            if (!currentOrder) return;

            // Get source services
            const sourceServices =
              this.getAvailableSourceServices(currentOrder);

            // Get current value
            const currentValue = field.formControl?.value;

            // Create options array
            const fieldOptions: FieldOption[] = [];

            // Add all source services to options
            sourceServices.forEach(service => {
              fieldOptions.push(
                createFieldOption(
                  `${service.serviceCode} - ${service.serviceName}`,
                  service.serviceCode
                )
              );
            });

            // Add current value if it's not already in options
            if (
              currentValue &&
              !sourceServices.some(s => s.serviceCode === currentValue)
            ) {
              const currentService = this.serviceOptions.find(
                s => s.serviceCode === currentValue
              );
              if (currentService) {
                fieldOptions.push(
                  createFieldOption(
                    `${currentService.serviceCode} - ${currentService.serviceName}`,
                    currentService.serviceCode
                  )
                );
              }
            }

            // Update the field options
            if (field.props) {
              field.props = {
                ...field.props,
                options: fieldOptions,
              };
              this.cdr.detectChanges();
            }
          };

          // Initial update
          updateSourceOptions();

          // Update when order changes
          const orderField = eval(
            `field.${pathConfig.fieldGroupPath}?.find(f => f.key === 'order')`
          );

          orderField?.formControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              updateSourceOptions();
            });

          // Also do a delayed update to handle preloaded values
          setTimeout(() => {
            updateSourceOptions();
          }, 100);
        },
      },
    };
  }

  private createSourceFieldSelect(
    includeAttachments = false
  ): FormlyFieldConfig {
    return {
      key: 'sourceField',
      type: 'select',
      props: {
        label: 'Source Field',
        required: true,
        placeholder: 'Select field from source service',
        options: [],
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const sourceServiceField = field.parent?.fieldGroup?.find(
            f => f.key === 'sourceService'
          );

          const updateSourceFieldOptions = (serviceCode: string) => {
            if (serviceCode) {
              let fields = this.getSourceServiceFields(serviceCode);

              if (includeAttachments) {
                const attachmentFields =
                  this.getSourceServiceAttachments(serviceCode);
                fields = [...fields, ...attachmentFields];
              }

              // Get current value if it exists
              const currentValue = field.formControl?.value;

              // Update options
              const fieldOptions = fields.map(f =>
                createFieldOption(f.fieldLabel, f.fieldName)
              );

              // If current value is not in fields but exists in model, add it
              if (
                currentValue &&
                !fields.some(f => f.fieldName === currentValue)
              ) {
                fieldOptions.push(
                  createFieldOption(currentValue, currentValue)
                );
              }

              updateFieldOptions(field, fieldOptions, this.cdr);
            }
          };

          // Handle initial value
          const initialSourceService = sourceServiceField?.formControl?.value;
          if (initialSourceService) {
            updateSourceFieldOptions(initialSourceService);
          }

          sourceServiceField?.formControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(serviceCode => {
              updateSourceFieldOptions(serviceCode);
            });
        },
      },
    };
  }

  private createAttachmentsMappingField(): FormlyFieldConfig {
    return {
      key: 'attachmentsMapping',
      fieldGroup: [
        this.createOfficerAttachmentField(),
        this.createCertificatesField(),
      ],
    };
  }

  private createOfficerAttachmentField(): FormlyFieldConfig {
    return {
      key: 'officerAttachment',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      props: {
        label: 'Officer Attachments Mapping',
        addText: 'Add Officer Attachment Mapping',
        titleFieldName: 'targetField',
        hideLabel: true,
      },
      hideExpression: (model: any, formState: any, field?: FormlyFieldConfig) =>
        createOrderBasedHideExpression(
          field?.parent?.parent?.parent?.model?.order
        ),
      fieldArray: {
        fieldGroup: [
          {
            fieldGroup: [
              this.createTargetAttachmentFieldSelect(ATTACHMENT_FIELD_PATH),
              this.createSourceServiceSelect(ATTACHMENT_FIELD_PATH),
              {
                key: 'attachmentKey',
                type: 'input',
                props: {
                  label: 'Source Field',
                  required: true,
                  placeholder: 'Attachment key from source service',
                },
              },
            ],
          },
        ],
      },
    };
  }

  private createCertificatesField(): FormlyFieldConfig {
    return {
      key: 'certificates',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      props: {
        label: 'Certificates Mapping',
        addText: 'Add Certificate Mapping',
        titleFieldName: 'targetField',
        hideLabel: true,
      },
      hideExpression: (model: any, formState: any, field?: FormlyFieldConfig) =>
        createOrderBasedHideExpression(
          field?.parent?.parent?.parent?.model?.order
        ),
      fieldArray: {
        fieldGroup: [
          {
            fieldGroup: [
              this.createTargetAttachmentFieldSelect(ATTACHMENT_FIELD_PATH),
              this.createSourceServiceSelect(ATTACHMENT_FIELD_PATH),
              {
                key: 'language',
                type: 'input',
                props: {
                  label: 'Language',
                  required: true,
                  placeholder: 'e.g., en-US, fr-FR',
                },
              },
              {
                key: 'certificateCode',
                type: 'input',
                props: {
                  label: 'Certificate Code',
                  required: true,
                  placeholder: 'e.g., CERTIFICATE_CODE',
                },
              },
            ],
          },
        ],
      },
    };
  }

  private createTargetAttachmentFieldSelect(
    pathConfig: FieldPathConfig = DEFAULT_FIELD_PATH
  ): FormlyFieldConfig {
    return {
      key: 'targetField',
      type: 'select',
      props: {
        label: 'Target Attachment Field',
        required: true,
        placeholder: 'Attachment field in current service',
        options: [],
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const getCurrentServiceCode = () => {
            const path = pathConfig.modelPath;
            return eval(`field.${path}?.serviceCode`);
          };

          const updateTargetFieldOptions = () => {
            const currentServiceCode = getCurrentServiceCode();
            if (currentServiceCode) {
              const currentAttachments =
                this.getCurrentServiceAttachments(currentServiceCode);

              // Get current value if it exists
              const currentValue = field.formControl?.value;

              // Create options array with all fields
              const fieldOptions: FieldOption[] = currentAttachments.map(f =>
                createFieldOption(f.fieldLabel, f.fieldName)
              );

              // If the current value isn't in the options but exists in model, ensure it's added
              if (
                currentValue &&
                !currentAttachments.some(f => f.fieldName === currentValue)
              ) {
                fieldOptions.push(
                  createFieldOption(currentValue, currentValue)
                );
              }

              // Update the field options directly
              if (field.props) {
                field.props.options = fieldOptions;
                this.cdr.detectChanges();
              }
            }
          };

          // Initial update
          updateTargetFieldOptions();

          // Update when service changes
          const serviceField = eval(
            `field.${pathConfig.fieldGroupPath}?.find(f => f.key === 'serviceCode')`
          );

          serviceField?.formControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              updateTargetFieldOptions();
            });
        },
      },
    };
  }

  private createStateMappingField(): FormlyFieldConfig {
    return {
      key: 'stateMapping',
      fieldGroup: [
        this.createWorkflowStateSelect(
          {
            modelPath: 'parent?.parent?.model',
            fieldGroupPath: 'parent?.parent?.fieldGroup',
          },
          {
            key: 'approve',
            label: 'Approve State',
            placeholder: 'State to trigger on parent when approved',
          }
        ),
        this.createWorkflowStateSelect(
          {
            modelPath: 'parent?.parent?.model',
            fieldGroupPath: 'parent?.parent?.fieldGroup',
          },
          {
            key: 'reject',
            label: 'Reject State',
            placeholder: 'State to trigger on parent when rejected',
          }
        ),
      ],
    };
  }

  // Helper method to create a workflow state selector field
  private createWorkflowStateSelect(
    pathConfig: FieldPathConfig = DEFAULT_FIELD_PATH,
    additionalConfig: {
      key: string;
      label: string;
      placeholder: string;
      multiple?: boolean;
    }
  ): FormlyFieldConfig {
    return {
      key: additionalConfig.key,
      type: additionalConfig.multiple
        ? 'customdropdownmultipleselector'
        : 'select',
      props: {
        label: additionalConfig.label,
        required: true,
        placeholder: additionalConfig.placeholder,
        options: [],
        multiple: additionalConfig.multiple,
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          // Function to get the current service code from the form's context
          const getCurrentServiceCode = (): string | undefined => {
            const path = pathConfig.modelPath;
            const serviceCode = eval(`field.${path}?.serviceCode`);

            return serviceCode;
          };

          // Function to update the options whenever the service changes
          const updateWorkflowStateOptions = () => {
            const serviceCode = getCurrentServiceCode();
            if (!serviceCode) {
              return;
            }

            const service = this.getServiceByCode(serviceCode);

            if (service?.workflowStates?.length) {
              // Use the workflow states loaded by loadServiceWorkflowStates
              const options: FieldOption[] = service.workflowStates.map(state =>
                createFieldOption(state, state)
              );

              if (field.props) {
                field.props.options = options;
                this.cdr.detectChanges();
              }
            } else if (service && service.changeRequestId) {
              // If workflow states aren't loaded, trigger loading them
              // Clear options while loading
              if (field.props) {
                field.props.options = [];
                this.cdr.detectChanges();
              }

              // Set up a one-time subscriber to update when workflow states are loaded
              const subscription = this.serviceOptionsChange
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  const updatedService = this.getServiceByCode(serviceCode);

                  if (updatedService?.workflowStates?.length) {
                    const options: FieldOption[] =
                      updatedService.workflowStates.map(state =>
                        createFieldOption(state, state)
                      );

                    if (field.props) {
                      field.props.options = options;
                      this.cdr.detectChanges();
                    }

                    subscription.unsubscribe();
                  }
                });

              // Load the workflow states
              this.loadServiceWorkflowStates(
                serviceCode,
                service.changeRequestId
              );
            }
          };

          // Initial update
          updateWorkflowStateOptions();

          // Get the service field to subscribe to its changes
          const serviceField = eval(
            `field.${pathConfig.fieldGroupPath}?.find(f => f.key === 'serviceCode')`
          );

          // Subscribe to service field changes
          if (serviceField?.formControl) {
            serviceField.formControl.valueChanges
              .pipe(
                debounceTime(100),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
              )
              .subscribe((serviceCode: string) => {
                updateWorkflowStateOptions();
              });
          }

          // Force an update when existing values are loaded
          setTimeout(() => {
            updateWorkflowStateOptions();
          }, 500);
        },
      },
    };
  }

  // Utility function to ensure all service codes in the model are present in the options arrays
  private ensureModelServicesInOptions(): void {
    if (!this.model || !this.model.serviceSequence) return;
    const modelServiceCodes = this.model.serviceSequence.map(
      s => s.serviceCode
    );

    // Helper to add missing services to an options array
    const addMissingServices = (optionsArr: any[]) => {
      modelServiceCodes.forEach(code => {
        if (code && !optionsArr.some(opt => opt.serviceCode === code)) {
          // Try to find the service in dropdownOptions or serviceOptions
          let found =
            this.dropdownOptions.find(opt => opt.serviceCode === code) ||
            this.serviceOptions.find(opt => opt.serviceCode === code);
          if (!found) {
            // If not found, create a minimal option from the model
            const modelService = this.model.serviceSequence.find(
              s => s.serviceCode === code
            );
            if (modelService) {
              found = {
                serviceCode: modelService.serviceCode,
                serviceName:
                  modelService.serviceName || modelService.serviceCode,
                changeRequestId: modelService.changeRequestId,
                id: modelService.serviceCode, // Use serviceCode as id fallback
              };
            }
          }
          if (found) {
            optionsArr.push(found);
          }
        }
      });
    };

    addMissingServices(this.serviceOptions);
    addMissingServices(this.dropdownOptions);
  }
}
